import { IcHeight, IcPin, IcStatusEllipse, IcStatusLosange } from '@assets/icons/24x24';
import { Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectHeightUnit } from '@redux/settings/settings.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { getAlertLevelColor, isManualAlert, isSpaceViolationAlert } from '@utils/data/notification.utils';
import { formatDate } from '@utils/date.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { AlertType, NotificationTypeEnum } from '@/types/data/data.types';
import { DateTimeEnum } from '@/types/dateTime.types';

type Props = {
  alert: AlertType;
  notificationType: NotificationTypeEnum;
  isArchived?: boolean;
};

export default function Alert({ alert, notificationType, isArchived = false }: Readonly<Props>) {
  const color = getAlertLevelColor(alert.level);
  const situationTime = useAppSelector(selectSituationTime) ?? Date.now();
  const heightUnit = useAppSelector(selectHeightUnit);

  const endTime = alert.endTime ?? situationTime;
  const formattedStarDate = formatDate(alert.startTime, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(endTime, DateTimeEnum.DATE);
  const formattedStartTime = format(new Date(alert.startTime), "HH':'mm':'ss");
  const formattedEndTime = format(new Date(endTime), "HH':'mm':'ss");
  const isSameDay = formattedStarDate === formattedEndDate;

  return (
    <HStack
      gap={1.5}
      width="100%"
      padding={3}
      backgroundColor={isArchived ? 'neutral.900' : 'neutral.800'}
      alignItems="start"
    >
      <Icon
        as={notificationType === NotificationTypeEnum.DRONE ? IcStatusLosange : IcStatusEllipse}
        boxSize="24px"
        color={color}
      />
      <Flex gap={isArchived ? 4 : 0.5} width="100%" alignItems="start" flexDirection={isArchived ? 'row' : 'column'}>
        <Text textStyle="lg" color={color}>
          <FormattedMessage id={`alert.level.${isManualAlert(alert) ? `MANUAL_${alert.level}` : alert.level}`} />
        </Text>
        <Text color="neutral.300" textStyle="lg" whiteSpace="pre-line">
          <FormattedMessage
            id={isSameDay ? 'historyPopup.sameDay' : 'historyPopup.multipleDays'}
            values={{
              startDate: (
                <Text key="startDate" as="span" textStyle="lg" color="neutral.white">
                  {formattedStarDate}
                </Text>
              ),
              startTime: (
                <Text key="startTime" as="span" textStyle="lg" color="neutral.white">
                  {formattedStartTime}
                </Text>
              ),
              endDate: (
                <Text key="endDate" as="span" textStyle="lg" color="neutral.white">
                  {formattedEndDate}
                </Text>
              ),
              endTime: (
                <Text key="endTime" as="span" textStyle="lg" color="neutral.white">
                  {formattedEndTime}
                </Text>
              ),
            }}
          />
        </Text>
        <HStack width="100%" flex={1} gap={1} alignItems="start">
          {isSpaceViolationAlert(alert) && (
            <>
              <HStack flex={1} gap={1}>
                <Icon as={IcHeight} boxSize="24px" color="neutral.300" />
                <VStack paddingTop={0.25} gap={0.25} alignItems="start">
                  <HStack gap={1}>
                    <Text color="neutral.300" textStyle="md">
                      <FormattedMessage id="global.min" />
                    </Text>
                    <Text color="neutral.white" textStyle="md">
                      {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, alert.heightMin))}
                    </Text>
                  </HStack>
                  <HStack gap={1}>
                    <Text color="neutral.300" textStyle="md">
                      <FormattedMessage id="global.max" />
                    </Text>
                    <Text color="neutral.white" textStyle="md">
                      {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, alert.heightMax))}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>
              <HStack flex={1} gap={1}>
                <Icon as={IcPin} boxSize="24px" color="neutral.300" />
                <Text textStyle="md" flex={1}>
                  {alert.zoneName}
                </Text>
              </HStack>
            </>
          )}
        </HStack>
      </Flex>
    </HStack>
  );
}
